import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { DataTable } from '../ui/DataTable'
import type { CatalogEntry, AISuggestion } from '../../utils/equipmentApi'

interface AICatalogPanelProps {
  catalogEntries: CatalogEntry[]
  isLoading: boolean
  onCatalogSelect: (entry: CatalogEntry) => void
  onAISuggest: (query: string, type: string | undefined, targetU: number | undefined, onProgress: (phase: string) => void, signal?: AbortSignal) => Promise<AISuggestion>
  onSearch: (params: { manufacturer?: string; equipment_type?: string; query?: string }) => void
}

const FILTER_PRESETS = [
  { label: 'HP/HPE', manufacturer: 'HPE' },
  { label: 'Huawei', manufacturer: 'Huawei' },
  { label: 'Cisco', manufacturer: 'Cisco' },
  { label: 'Arista', manufacturer: 'Arista' },
  { label: 'Servers', equipment_type: 'server' },
  { label: 'Switches', equipment_type: 'switch' },
  { label: 'Firewalls', equipment_type: 'firewall' },
  { label: 'Routers', equipment_type: 'router' },
]


export function AICatalogPanel({
  catalogEntries,
  isLoading,
  onCatalogSelect,
  onAISuggest,
  onSearch,
}: AICatalogPanelProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<string>('')
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>('')
  const [aiQuery, setAiQuery] = useState('')
  const [aiSuggestion, setAiSuggestion] = useState<AISuggestion | null>(null)
  const [isAISearching, setIsAISearching] = useState(false)
  const [aiPhase, setAiPhase] = useState('Запуск ИИ...')

  const manufacturers = useMemo(() => {
    const set = new Set<string>()
    catalogEntries.forEach(e => set.add(e.manufacturer))
    return Array.from(set).sort()
  }, [catalogEntries])

  const types = useMemo(() => {
    const set = new Set<string>()
    catalogEntries.forEach(e => set.add(e.equipment_type))
    return Array.from(set).sort()
  }, [catalogEntries])

  const handlePresetClick = useCallback((preset: typeof FILTER_PRESETS[number]) => {
    if (preset.manufacturer) {
      setSelectedManufacturer(preset.manufacturer)
      onSearch({ manufacturer: preset.manufacturer })
    } else if (preset.equipment_type) {
      setSelectedType(preset.equipment_type)
      onSearch({ equipment_type: preset.equipment_type })
    }
  }, [onSearch])

  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => {
      // Abort any pending AI suggestion when panel unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const handleAISearch = useCallback(async () => {
    if (!aiQuery.trim()) return
    setIsAISearching(true)
    setAiPhase('Инициализация ИИ-агента...')

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const suggestion = await onAISuggest(aiQuery, selectedType || undefined, undefined, (phase) => {
        setAiPhase(phase)
      }, abortController.signal)
      setAiSuggestion(suggestion)
    } catch (err: any) {
      if (err.message === 'Aborted' || err.name === 'CanceledError') {
        console.log('AI search aborted')
      } else {
        console.error('AI search failed:', err)
      }
    } finally {
      if (abortControllerRef.current === abortController) {
        setIsAISearching(false)
      }
    }
  }, [aiQuery, selectedType, onAISuggest])

  const handleSearch = useCallback(() => {
    onSearch({
      manufacturer: selectedManufacturer || undefined,
      equipment_type: selectedType || undefined,
      query: searchQuery || undefined,
    })
  }, [selectedManufacturer, selectedType, searchQuery, onSearch])

  const columns = [
    { key: 'manufacturer' as const, label: 'Производитель', width: '120px' },
    { key: 'model' as const, label: 'Модель', width: 'auto' },
    { key: 'equipment_type' as const, label: 'Тип', width: '100px' },
    { key: 'u_height' as const, label: 'U', width: '50px' },
    { key: 'weight_kg' as const, label: 'Вес (кг)', width: '70px' },
    { key: 'verified' as const, label: 'Вериф.', width: '70px' },
  ]

  const rows = catalogEntries.map(entry => ({
    id: entry.id.toString(),
    manufacturer: entry.manufacturer,
    model: entry.model,
    equipment_type: entry.equipment_type,
    u_height: entry.u_height,
    weight_kg: entry.weight_kg ?? '-',
    verified: entry.verified ? 'Yes' : '—',
  }))

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Поиск по названию..."
            className="flex-1 px-3 py-2 rounded-lg border bg-bg-input border-border text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90"
          >
            Поиск
          </button>
        </div>

        {/* Quick presets */}
        <div className="flex flex-wrap gap-2">
          {FILTER_PRESETS.map(preset => (
            <button
              key={preset.label}
              onClick={() => handlePresetClick(preset)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                (preset.manufacturer === selectedManufacturer || preset.equipment_type === selectedType)
                  ? 'bg-accent text-white'
                  : 'bg-bg-panel border border-border text-text-secondary hover:bg-bg-hover'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* AI Suggestion */}
      <div className="rounded-lg bg-bg-panel border border-border p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-medium">AI Recommendation</span>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Опишите нужное оборудование (напр. 'сервер для VMware 2U')"
            className="flex-1 px-3 py-2 rounded-lg border bg-bg-input border-border text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500"
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAISearch()}
          />
          <button
            onClick={handleAISearch}
            disabled={isAISearching}
            className="px-4 py-2 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-300 hover:bg-purple-500/30 disabled:opacity-50"
          >
            {isAISearching ? '...' : 'AI'}
          </button>
        </div>

        {isAISearching && (
          <div className="mt-3 p-3 text-sm text-purple-300 bg-purple-500/10 rounded border border-purple-500/20 flex items-center space-x-2">
            <svg className="animate-spin h-4 w-4 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>AI: {aiPhase}</span>
          </div>
        )}

        {aiSuggestion && (
          <div className="mt-3 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
            <div className="text-xs text-purple-300/80 mb-1">Рекомендация:</div>
            <div className="text-sm text-text-secondary whitespace-pre-wrap">
              {aiSuggestion.reasoning}
            </div>
            <div className="text-xs text-purple-400 mt-2 flex justify-between items-center">
              <span>Confidence: {(aiSuggestion.confidence_score * 100).toFixed(0)}%</span>
            </div>
            {aiSuggestion.suggested_equipment && (
              <button
                className="mt-3 w-full px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded-lg text-sm transition-colors shadow shadow-purple-900/50"
                onClick={() => onCatalogSelect(aiSuggestion.suggested_equipment!)}
              >
                Применить {aiSuggestion.suggested_equipment.manufacturer} {aiSuggestion.suggested_equipment.model}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-xs text-text-muted">
        <span>Всего: {catalogEntries.length}</span>
        <span>Производителей: {manufacturers.length}</span>
        <span>Типов: {types.length}</span>
      </div>

      {/* Catalog Table */}
      <DataTable
        columns={columns}
        data={rows}
        loading={isLoading}
        emptyText="Нет записей в каталоге"
        selectable={false}
      />
    </div>
  )
}
